/**
 * Stato globale della sessione.
 *
 * Zustand è usato **solo** qui e per le preferenze d'interfaccia: tutto il resto
 * dei dati vive in TanStack Query, che ne gestisce cache, invalidazione e stati
 * di caricamento. Duplicare i dati del server in uno store globale è la via più
 * rapida per ritrovarsi con due verità in disaccordo.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { getGateway } from '@/lib/data';
import { logError } from '@/lib/observability';
import {
  acceptPendingInvitations,
  checkPlatformAdmin,
  getCurrentUser,
  loadContexts,
  signOut as authSignOut,
  type AuthenticatedUser,
} from '@/domains/auth/auth-service';
import { hasPermission, type Permission } from '@/domains/auth/permissions';
import type { Location, MyContext } from '@/types/domain';

export type SessionStatus = 'idle' | 'loading' | 'authenticated' | 'anonymous' | 'error';

interface SessionState {
  status: SessionStatus;
  user: AuthenticatedUser | null;
  contexts: MyContext[];
  /** Amministratore della piattaforma: gestisce ristoranti e titolari, non i loro dati. */
  isPlatformAdmin: boolean;
  activeOrganizationId: string | null;
  activeLocationId: string | null;
  locations: Location[];
  error: string | null;
  /** Messaggio di avanzamento durante l'inizializzazione del backend dimostrativo. */
  bootMessage: string | null;

  initialize(): Promise<void>;
  refreshContext(): Promise<void>;
  setActiveOrganization(organizationId: string): Promise<void>;
  setActiveLocation(locationId: string): void;
  setBootMessage(message: string | null): void;
  signOut(): Promise<void>;
}

/** Preferenze locali persistite, separate dalla sessione. */
interface PreferencesState {
  theme: 'light' | 'dark' | 'system';
  lastOrganizationId: string | null;
  lastLocationId: string | null;
  kdsSoundEnabled: boolean;
  setTheme(theme: 'light' | 'dark' | 'system'): void;
  rememberSelection(organizationId: string | null, locationId: string | null): void;
  setKdsSound(enabled: boolean): void;
}

export const usePreferences = create<PreferencesState>()(
  persist(
    (set) => ({
      theme: 'system',
      lastOrganizationId: null,
      lastLocationId: null,
      kdsSoundEnabled: true,
      setTheme: (theme) => set({ theme }),
      rememberSelection: (organizationId, locationId) =>
        set({ lastOrganizationId: organizationId, lastLocationId: locationId }),
      setKdsSound: (kdsSoundEnabled) => set({ kdsSoundEnabled }),
    }),
    { name: 'bacco.preferences' },
  ),
);

async function fetchLocations(organizationId: string): Promise<Location[]> {
  const gateway = await getGateway();
  return gateway.select<Location>('locations', {
    filters: [
      { column: 'organization_id', operator: 'eq', value: organizationId },
      { column: 'status', operator: 'eq', value: 'active' },
    ],
    order: [{ column: 'name' }],
  });
}

/** Un ristorante sospeso o cessato non concede permessi: il database li nega. */
export function isOrganizationUsable(context: Pick<MyContext, 'organization_status'>): boolean {
  return context.organization_status === 'active' || context.organization_status === 'trial';
}

export const useSession = create<SessionState>((set, get) => ({
  status: 'idle',
  user: null,
  contexts: [],
  isPlatformAdmin: false,
  activeOrganizationId: null,
  activeLocationId: null,
  locations: [],
  error: null,
  bootMessage: null,

  setBootMessage: (bootMessage) => set({ bootMessage }),

  async initialize() {
    if (get().status === 'loading') return;
    set({ status: 'loading', error: null });

    try {
      const user = await getCurrentUser();
      if (!user) {
        set({ status: 'anonymous', user: null, contexts: [], locations: [] });
        return;
      }

      // Chi entra per la prima volta con l'indirizzo invitato si ritrova nel
      // ristorante senza passaggi ulteriori. Un errore qui non blocca l'accesso.
      try {
        await acceptPendingInvitations();
      } catch (error) {
        logError(error, { source: 'accept_my_invitations' });
      }

      const [contexts, isPlatformAdmin] = await Promise.all([loadContexts(), checkPlatformAdmin(user.id)]);
      if (contexts.length === 0) {
        // Utente autenticato ma senza organizzazione: deve accettare un invito,
        // oppure è l'amministratore della piattaforma. Non è un errore.
        set({ status: 'authenticated', user, contexts: [], isPlatformAdmin, locations: [] });
        return;
      }

      const preferences = usePreferences.getState();
      // Un ristorante sospeso resta nell'elenco per spiegarlo, ma non si apre
      // per primo se ce n'è uno utilizzabile.
      const usable = contexts.filter((context) => isOrganizationUsable(context));
      const candidates = usable.length > 0 ? usable : contexts;
      const preferred =
        candidates.find((context) => context.organization_id === preferences.lastOrganizationId) ??
        candidates[0]!;

      const locations = await fetchLocations(preferred.organization_id);
      const preferredLocation =
        locations.find((location) => location.id === preferences.lastLocationId) ?? locations[0] ?? null;

      set({
        status: 'authenticated',
        user,
        contexts,
        isPlatformAdmin,
        activeOrganizationId: preferred.organization_id,
        activeLocationId: preferredLocation?.id ?? null,
        locations,
      });
      preferences.rememberSelection(preferred.organization_id, preferredLocation?.id ?? null);
    } catch (error) {
      set({
        status: 'error',
        error: error instanceof Error ? error.message : 'Impossibile inizializzare la sessione.',
      });
    }
  },

  async refreshContext() {
    const { activeOrganizationId } = get();
    if (!activeOrganizationId) return;
    const [contexts, locations] = await Promise.all([
      loadContexts(),
      fetchLocations(activeOrganizationId),
    ]);
    set({ contexts, locations });
  },

  async setActiveOrganization(organizationId) {
    const locations = await fetchLocations(organizationId);
    const locationId = locations[0]?.id ?? null;
    set({ activeOrganizationId: organizationId, activeLocationId: locationId, locations });
    usePreferences.getState().rememberSelection(organizationId, locationId);
  },

  setActiveLocation(locationId) {
    set({ activeLocationId: locationId });
    usePreferences.getState().rememberSelection(get().activeOrganizationId, locationId);
  },

  async signOut() {
    await authSignOut();
    usePreferences.getState().rememberSelection(null, null);
    set({
      status: 'anonymous',
      user: null,
      isPlatformAdmin: false,
      contexts: [],
      activeOrganizationId: null,
      activeLocationId: null,
      locations: [],
    });
  },
}));

// -----------------------------------------------------------------------------
// Selettori
// -----------------------------------------------------------------------------

export function useActiveContext(): MyContext | null {
  return useSession((state) => {
    const { contexts, activeOrganizationId } = state;
    return contexts.find((context) => context.organization_id === activeOrganizationId) ?? null;
  });
}

export function useActiveLocation(): Location | null {
  return useSession((state) => {
    const { locations, activeLocationId } = state;
    return locations.find((location) => location.id === activeLocationId) ?? null;
  });
}

/**
 * Verifica un permesso per nascondere i comandi non disponibili.
 * Non è un controllo di sicurezza: quello vive nel database.
 */
export function usePermission(permission: Permission): boolean {
  const context = useActiveContext();
  return context ? hasPermission(context.permissions, permission) : false;
}

export function usePermissions(): readonly string[] {
  const context = useActiveContext();
  return context?.permissions ?? [];
}
